/**
 * Control actions from keyboard and controller buttons (SPEC §5.1, §14.2).
 * Shift up/down: change virtual gear in VS; in ERG they adjust the target ±5 W (SPEC §6.4).
 */
import type { ControlAction } from '@/store/settingsModel';
import { Emitter } from '@/trainer/types';
import { getSession } from './manager';

export const cameraEvents = new Emitter<void>();
export const actionEvents = new Emitter<ControlAction>();

/** Optional hooks set by the VS module (P5) and the workout runner (P3). */
export const actionHooks: {
  shift?: (dir: 1 | -1) => void;
  skipInterval?: () => void;
  ergAdjust?: (deltaW: number) => boolean;
} = {};

export function dispatchAction(action: ControlAction): void {
  const s = getSession();
  actionEvents.emit(action);
  switch (action) {
    case 'shiftUp':
    case 'shiftDown': {
      const dir = action === 'shiftUp' ? 1 : -1;
      if (s && s.effectiveMode === 'erg') {
        ergAdjust(dir * 5);
      } else {
        actionHooks.shift?.(dir);
      }
      break;
    }
    case 'ergUp':
      ergAdjust(5);
      break;
    case 'ergDown':
      ergAdjust(-5);
      break;
    case 'skip':
      actionHooks.skipInterval?.();
      break;
    case 'pause':
      s?.togglePause();
      break;
    case 'lap':
      s?.manualLap();
      break;
    case 'camera':
      cameraEvents.emit();
      break;
    case 'none':
      break;
  }
}

function ergAdjust(delta: number): void {
  // Workouts apply the adjustment to the runner target; otherwise to the manual ERG target.
  if (actionHooks.ergAdjust?.(delta)) return;
  getSession()?.adjustErg(delta);
}

/** Keyboard map (always on, SPEC §5.1 / §14.2). Esc is handled by the ride screen (menu). */
export function keyToAction(e: Pick<KeyboardEvent, 'key' | 'code'>): ControlAction | null {
  switch (e.key) {
    case 'ArrowRight':
      return 'shiftUp';
    case 'ArrowLeft':
      return 'shiftDown';
    case 'ArrowUp':
      return 'ergUp';
    case 'ArrowDown':
      return 'ergDown';
    case ' ':
      return 'pause';
    case 'l':
    case 'L':
      return 'lap';
    case 'c':
    case 'C':
      return 'camera';
    default:
      return e.code === 'Space' ? 'pause' : null;
  }
}
